/**
 * UndoManager service for managing undo operations stack
 * Maintains a stack of last 10 operations with sessionStorage persistence
 * @module services/undoManager
 */

import { secureLogger } from '@/lib/secure-logger'

/**
 * Operation types supported by undo functionality
 */
export type UndoOperationType =
  | 'EMPLOYEE_STATUS_CHANGE'
  | 'MANAGER_REASSIGNMENT'
  | 'EMPLOYEE_UPDATE'
  | 'PAYROLL_ENTRY_CREATE'

/**
 * Entity types
 */
export type UndoEntityType = 'employee' | 'payroll'

/**
 * Serializable undo action data
 * Cannot store functions, so we store the API call parameters instead
 */
export interface UndoActionData {
  /** API name from Amplify config (e.g., 'workforce') */
  apiName: string
  /** HTTP method */
  method: 'GET' | 'POST' | 'PUT' | 'DELETE'
  /** API path */
  path: string
  /** Request body (for POST/PUT) */
  body?: Record<string, any>
}

/**
 * Undo operation interface
 */
export interface UndoOperation {
  /** Unique operation ID */
  id: string
  /** Operation type */
  type: UndoOperationType
  /** Entity type */
  entityType: UndoEntityType
  /** Entity ID (employee ID or payroll entry ID) */
  entityId: string
  /** Timestamp when operation was performed */
  timestamp: number
  /** Human-readable description for toast */
  description: string
  /** State before operation */
  beforeState: Record<string, any>
  /** State after operation */
  afterState: Record<string, any>
  /** Serializable undo action data */
  undoActionData: UndoActionData
  /** Timestamp when undo expires (for toast timeout) */
  expiresAt: number
}

/**
 * Maximum number of operations to maintain in stack
 */
const MAX_STACK_SIZE = 10

/**
 * Undo stack timeout in milliseconds (10 seconds for toast display)
 */
const UNDO_TIMEOUT_MS = 10000

/**
 * UndoStack class manages the undo operations stack
 */
export class UndoStack {
  private operations: UndoOperation[] = []
  private storageKey: string

  /**
   * Create a new UndoStack
   * @param tenantDomain - Tenant domain for scoping sessionStorage
   */
  constructor(tenantDomain: string) {
    this.storageKey = `workforce-undo-stack-${tenantDomain}`
    this.restore()
  }

  /**
   * Push a new operation to the stack
   * Automatically removes oldest operation if stack exceeds max size
   */
  push(operation: UndoOperation): void {
    secureLogger.info('Pushing operation to undo stack', {
      id: operation.id,
      type: operation.type,
      entityId: operation.entityId
    })

    // Add operation to stack
    this.operations.push(operation)

    // Remove oldest operation if exceeding max size (FIFO)
    if (this.operations.length > MAX_STACK_SIZE) {
      const removed = this.operations.shift()
      secureLogger.info('Removed oldest operation from stack (max size exceeded)', {
        removedId: removed?.id,
        remainingCount: this.operations.length
      })
    }

    // Persist to sessionStorage
    this.persist()
  }

  /**
   * Pop the most recent operation from the stack
   * Returns null if stack is empty
   */
  pop(): UndoOperation | null {
    const operation = this.operations.pop()

    if (operation) {
      secureLogger.info('Popped operation from undo stack', {
        id: operation.id,
        type: operation.type,
        remainingCount: this.operations.length
      })
      this.persist()
    }

    return operation || null
  }

  /**
   * Peek at the most recent operation without removing it
   * Returns null if stack is empty
   */
  peek(): UndoOperation | null {
    return this.operations[this.operations.length - 1] || null
  }

  /**
   * Get all operations in the stack (oldest to newest)
   */
  getAll(): UndoOperation[] {
    return [...this.operations]
  }

  /**
   * Clear all operations from the stack
   */
  clear(): void {
    secureLogger.info('Clearing undo stack', {
      operationCount: this.operations.length
    })

    this.operations = []
    this.persist()
  }

  /**
   * Check if undo operations are available
   */
  canUndo(): boolean {
    return this.operations.length > 0
  }

  /**
   * Get the number of operations in the stack
   */
  size(): number {
    return this.operations.length
  }

  /**
   * Remove a specific operation by ID
   */
  remove(id: string): boolean {
    const initialLength = this.operations.length
    this.operations = this.operations.filter(op => op.id !== id)

    if (this.operations.length < initialLength) {
      secureLogger.info('Removed operation from stack', {
        id,
        remainingCount: this.operations.length
      })
      this.persist()
      return true
    }

    return false
  }

  /**
   * Persist stack to sessionStorage
   * @private
   */
  private persist(): void {
    try {
      const serialized = JSON.stringify(this.operations)
      sessionStorage.setItem(this.storageKey, serialized)
      secureLogger.debug('Persisted undo stack to sessionStorage', {
        operationCount: this.operations.length
      })
    } catch (error) {
      secureLogger.error('Failed to persist undo stack to sessionStorage', {
        error: error instanceof Error ? error.message : 'Unknown error'
      })
    }
  }

  /**
   * Restore stack from sessionStorage
   * @private
   */
  private restore(): void {
    try {
      const serialized = sessionStorage.getItem(this.storageKey)

      if (serialized) {
        const operations = JSON.parse(serialized) as UndoOperation[]
        this.operations = operations
      }
    } catch (error) {
      secureLogger.error('Failed to restore undo stack from sessionStorage', {
        error: error instanceof Error ? error.message : 'Unknown error'
      })
      // Reset to empty stack on error
      this.operations = []
    }
  }
}

/**
 * Generate a unique ID for undo operations
 */
export function generateUndoId(): string {
  return `undo-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`
}

/**
 * Create an undo operation
 */
export function createUndoOperation(params: {
  type: UndoOperationType
  entityType: UndoEntityType
  entityId: string
  description: string
  beforeState: Record<string, any>
  afterState: Record<string, any>
  undoActionData: UndoActionData
}): UndoOperation {
  const now = Date.now()

  return {
    id: generateUndoId(),
    timestamp: now,
    expiresAt: now + UNDO_TIMEOUT_MS,
    ...params
  }
}

/**
 * Singleton UndoManager instance
 */
class UndoManager {
  private stack: UndoStack | null = null

  /**
   * Initialize the undo manager with tenant domain
   * Must be called before using any other methods
   */
  initialize(tenantDomain: string): void {
    if (this.stack) {
      secureLogger.warn('UndoManager already initialized, replacing stack')
    }

    this.stack = new UndoStack(tenantDomain)
  }

  /**
   * Get the undo stack
   * Throws error if not initialized
   */
  getStack(): UndoStack {
    if (!this.stack) {
      throw new Error('UndoManager not initialized. Call initialize(tenantDomain) first.')
    }
    return this.stack
  }

  /**
   * Check if manager is initialized
   */
  isInitialized(): boolean {
    return this.stack !== null
  }

  /**
   * Clear the current stack and reset
   * Used when switching tenants or logging out
   */
  reset(): void {
    if (this.stack) {
      this.stack.clear()
      this.stack = null
      secureLogger.info('UndoManager reset')
    }
  }
}

// Export singleton instance
export const undoManager = new UndoManager()
